import {
  type CoverageQuote,
  getTotalPremium,
  isPricedAsOneTotal,
  type OneTimeFee,
  type PricedQuoteCard,
  type QuoteCardData,
  QuoteCardStage,
} from './quoteCardTypes'

/** One carrier's added coverages, as the cart sidebar lists them. */
export interface QuoteCartQuote {
  id: string
  carrierName: string
  carrierProgram?: string | null
  iconUrl: string
  iconAlt: string
  coverages: CoverageQuote[]
  /** The carrier's charges on top of the coverages — only the ones inside {@link subtotal}. */
  oneTimeFees: OneTimeFee[]
  /** Cart keys for the coverages listed here — what this quote's Remove drops. */
  keys: string[]
  subtotal: number
  /** Judged on the whole card, not just what's in the cart, so the cart labels a line as the card does. */
  pricedAsOneTotal: boolean
}

/**
 * The cart entry for one coverage on one card, keyed on the indication that priced it rather
 * than on the card. A grouped card speaks for several indications, and this key is what
 * carries them to checkout, where `indicationIdsFromKeys` splits out the ids to apply.
 */
export function coverageCartKey(cardId: string, coverage: CoverageQuote): string {
  return `${coverage.indicationId ?? cardId}:${coverage.type}`
}

/** Every cart key a card's coverages occupy — its bundle "Add to cart", and its in-cart test. */
export function cardCartKeys(quote: PricedQuoteCard): string[] {
  return quote.coverages.map((coverage) => coverageCartKey(quote.id, coverage))
}

/** The offer a coverage or fee belongs to. A grouped card holds several; an ungrouped one is its own. */
function offerOf(item: { indicationId?: string }, quote: PricedQuoteCard): string {
  return item.indicationId ?? quote.id
}

/**
 * Whether each coverage on this card can be bought on its own. Checkout applies whole
 * indications, so two coverages priced on one indication can only go to the broker together
 * — offering them separately would send a selection the customer never made.
 */
export function coveragesAreSeparatelyPurchasable(quote: PricedQuoteCard): boolean {
  const offers = new Set(quote.coverages.map((coverage) => offerOf(coverage, quote)))
  return offers.size === quote.coverages.length
}

/**
 * The carrier charges its one-time fees against a whole offer, so a fee joins the cart once
 * every coverage of the offer that carries it is in. Per offer, not per card: a grouped card
 * is several offers, and each one's fees are due as soon as that line is taken whole.
 */
function feesInsideSelection(quote: PricedQuoteCard, picked: CoverageQuote[]): OneTimeFee[] {
  const pickedKeys = new Set(picked.map((coverage) => coverageCartKey(quote.id, coverage)))
  const outstanding = new Set(
    quote.coverages
      .filter((coverage) => !pickedKeys.has(coverageCartKey(quote.id, coverage)))
      .map((coverage) => offerOf(coverage, quote))
  )
  const takenWhole = new Set(
    picked.map((coverage) => offerOf(coverage, quote)).filter((offer) => !outstanding.has(offer))
  )
  return (quote.oneTimeFees ?? []).filter((fee) => takenWhole.has(offerOf(fee, quote)))
}

/** The card's headline price once the whole quote is in the cart, so both print the same number;
 *  a subset is its own coverages plus whichever fees came with them. */
function subtotalFor(quote: PricedQuoteCard, picked: CoverageQuote[], fees: OneTimeFee[]): number {
  if (picked.length === quote.coverages.length) return getTotalPremium(quote)
  let subtotal = 0
  for (const offer of new Set(picked.map((coverage) => offerOf(coverage, quote)))) {
    const offerCoverages = quote.coverages.filter((coverage) => offerOf(coverage, quote) === offer)
    const pickedHere = picked.filter((coverage) => offerOf(coverage, quote) === offer)
    const offerTotal = quote.offerTotals?.[offer]
    // An offer taken whole costs its own total, which still counts a one-sum carrier's null lines.
    if (offerTotal !== undefined && pickedHere.length === offerCoverages.length) {
      subtotal += offerTotal
      continue
    }
    subtotal += pickedHere.reduce((sum, coverage) => sum + (coverage.premium ?? 0), 0)
    subtotal += fees.filter((fee) => offerOf(fee, quote) === offer).reduce((sum, fee) => sum + fee.amount, 0)
  }
  return subtotal
}

/** Groups cart keys into their quotes — pass every quote, since filters don't empty the cart. */
export function groupCartByQuote(cartKeys: ReadonlySet<string>, quotes: QuoteCardData[]): QuoteCartQuote[] {
  const groups: QuoteCartQuote[] = []
  for (const quote of quotes) {
    if (quote.stage !== QuoteCardStage.PRICED) continue
    const coverages = quote.coverages.filter((coverage) => cartKeys.has(coverageCartKey(quote.id, coverage)))
    if (coverages.length === 0) continue
    const oneTimeFees = feesInsideSelection(quote, coverages)
    groups.push({
      id: quote.id,
      carrierName: quote.carrierName,
      carrierProgram: quote.carrierProgram,
      iconUrl: quote.iconUrl,
      iconAlt: quote.iconAlt,
      coverages,
      oneTimeFees,
      keys: coverages.map((coverage) => coverageCartKey(quote.id, coverage)),
      subtotal: subtotalFor(quote, coverages, oneTimeFees),
      pricedAsOneTotal: isPricedAsOneTotal(quote.coverages),
    })
  }
  return groups
}

export function cartTotal(groups: QuoteCartQuote[]): number {
  return groups.reduce((sum, group) => sum + group.subtotal, 0)
}
